import { useCallback, useEffect, useRef, useState } from "react";
import { AttachmentsActionBarHoldItem } from "../MassTexts/Message/AttachmentsActionBarHoldItem";
import {
  PlanTypeV4,
  AttachmentSourceType,
  MassTextAttachmentId,
  MassTextAttachmentType,
} from "@markit/common.types";
import {
  defaultProductListView,
  getMassTextAttachmentLink,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import { useDispatch, useSelector } from "react-redux";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import MassTextAttachmentsPanel from "../MassTexts/Message/MassTextAttachmentsPanel";
import AttachmentTicketLinkItemPanel from "../MassTexts/Message/AttachmentTicketLinkItemPanel";

type ConversationAttachmentButtonProps = {
  message: string;
  setMessage: (message: string) => void;
  image: string;
  setImage: (image: string) => void;
  conversationMainUserId: string;
};

const ConversationAttachmentButton = (
  props: ConversationAttachmentButtonProps
) => {
  const { message, setMessage, image, setImage, conversationMainUserId } =
    props;
  const { accountData } = useSelector(getAccountSlice);
  const dispatch = useDispatch();
  const [showAttachmentModal, setShowAttachmentModal] =
    useState<boolean>(false);
  const [showAttachmentTicketModal, setShowAttachmentTicketModal] =
    useState<boolean>(false);
  const [defaultAttachmentListView, setDefaultAttachmentListView] = useState(0);
  const [defaultAttachmentId, setDefaultAttachmentId] = useState<
    string | undefined
  >(undefined);
  const [attachmentType, setAttachmentType] = useState<
    MassTextAttachmentType | undefined
  >();

  // States for image picker
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // this is to handle case where you remove the image and then select the same one. If we remove an image, we need to make sure to clear the file input ref so that the React.ChangeEvent gets triggered again
  useEffect(() => {
    if (image === "" && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [image]);

  const closeOnPress = useCallback(() => {
    setShowAttachmentModal(false);
    setShowAttachmentTicketModal(false);
    setAttachmentType(undefined);
    setDefaultAttachmentId(undefined);
    setDefaultAttachmentListView(0);
  }, []);

  const finalizeAttachmentOnPress = useCallback(
    (itemId: string) => {
      const attachmentLink = getMassTextAttachmentLink(
        itemId,
        accountData.trustedSender,
        accountData.username,
        attachmentType
      );
      if (itemId === MassTextAttachmentId.CONTACT_CARD) {
        setImage(accountData.customer.contactCardUrl);
      } else {
        const endsWithWhitespace = /\s$/.test(message);
        const messageWithEventURL =
          message +
          (message.length === 0 || endsWithWhitespace ? "" : " ") +
          attachmentLink;
        setMessage(messageWithEventURL);
      }

      closeOnPress();
    },
    [
      attachmentType,
      accountData.trustedSender,
      accountData.username,
      accountData.customer.contactCardUrl,
      closeOnPress,
      setImage,
      message,
      setMessage,
    ]
  );

  const addImageOnPress = useCallback(() => {
    if (
      !hasSubscriptionUnlockedAdvancedData(accountData) &&
      !accountData.isAdmin
    ) {
      dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name));
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [accountData, dispatch]);

  const onAttachmentClick = useCallback(
    (key: string) => {
      if (key === "media") {
        addImageOnPress();
      } else {
        setDefaultAttachmentListView(defaultProductListView[key] ?? 0);
        if (!(key in defaultProductListView)) setDefaultAttachmentId(key);
      }

      if (
        key === MassTextAttachmentId.TICKET_LINK ||
        key === MassTextAttachmentId.CALENDAR_INVITE
      ) {
        setAttachmentType(
          key === MassTextAttachmentId.TICKET_LINK
            ? MassTextAttachmentType.TICKET_LINK
            : MassTextAttachmentType.CALENDAR_INVITE
        );
        setShowAttachmentTicketModal(true);
      } else {
        setShowAttachmentModal(true);
      }
    },
    [addImageOnPress]
  );

  return (
    <>
      <AttachmentsActionBarHoldItem
        onClick={onAttachmentClick}
        attachmentSourceType={AttachmentSourceType.CONVERSATION}
        setImage={setImage}
        fileInputRef={fileInputRef}
        disabledEventLinks={!conversationMainUserId}
      />
      {showAttachmentModal ? (
        <MassTextAttachmentsPanel
          closeOnPress={closeOnPress}
          defaultListView={defaultAttachmentListView}
          defaultAttachmentId={defaultAttachmentId}
          finalizeAttachment={finalizeAttachmentOnPress}
        />
      ) : null}
      {showAttachmentTicketModal && attachmentType ? (
        <AttachmentTicketLinkItemPanel
          closeOnPress={closeOnPress}
          userId={conversationMainUserId}
          attachmentType={attachmentType}
          finalizeAttachment={finalizeAttachmentOnPress}
        />
      ) : null}
    </>
  );
};

export default ConversationAttachmentButton;
